<script setup>
import { computed, onUnmounted } from 'vue';
import { useToggle, useWindowSize } from '@vueuse/core';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import wootConstants from 'dashboard/constants/globals';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

// No props needed as we're getting currentChat from the store directly
const store = useStore();
const { t } = useI18n();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);
const { width: windowWidth } = useWindowSize();

const currentChat = computed(() => store.getters.getSelectedChat);
const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);
const isResolved = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.RESOLVED
);
const isPending = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.PENDING
);
const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);
const isOpen = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.OPEN
);

const caseCloseTypeOptions = computed(() => [
  {
    key: 'complete',
    label: t('CONVERSATION.CASE_CLOSING.TYPES.COMPLETE'),
    icon: 'i-lucide-check-check',
  },
  {
    key: 'no_response',
    label: t('CONVERSATION.CASE_CLOSING.TYPES.NO_RESPONSE'),
    icon: 'i-lucide-message-square-off',
  },
  {
    key: 'promo_complaint',
    label: t('CONVERSATION.CASE_CLOSING.TYPES.PROMO_COMPLAINT'),
    icon: 'i-lucide-badge-alert',
  },
  {
    key: 'contact_other_party',
    label: t('CONVERSATION.CASE_CLOSING.TYPES.CONTACT_OTHER_PARTY'),
    icon: 'i-lucide-users',
  },
]);

const baseActionMenuItems = computed(() => {
  const items = [];

  if (!currentChat.value.muted) {
    items.push({
      icon: 'i-lucide-volume-off',
      label: t('CONTACT_PANEL.MUTE_CONTACT'),
      action: 'mute',
      value: 'mute',
    });
  } else {
    items.push({
      icon: 'i-lucide-volume-1',
      label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
      action: 'unmute',
      value: 'unmute',
    });
  }

  items.push({
    icon: 'i-lucide-share',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  return items;
});

const resolveMenuItems = computed(() => {
  const items = [];

  if (isOpen.value) {
    caseCloseTypeOptions.value.forEach(option => {
      items.push({
        icon: option.icon,
        label: option.label,
        action: 'resolve_case',
        value: option.key,
      });
    });
  } else if (isResolved.value) {
    items.push({
      icon: 'i-lucide-rotate-ccw',
      label: t('CONVERSATION.HEADER.REOPEN_ACTION'),
      action: 'open_conversation',
      value: 'reopen',
    });
  } else if (isPending.value || isSnoozed.value) {
    items.push({
      icon: 'i-lucide-circle-play',
      label: t('CONVERSATION.HEADER.OPEN_ACTION'),
      action: 'open_conversation',
      value: 'open',
    });
  }

  if (!isPending.value) {
    items.push({
      icon: 'i-lucide-alarm-clock-minus',
      label: t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE_UNTIL'),
      action: 'snooze_conversation',
      value: 'snooze',
    });

    items.push({
      icon: 'i-lucide-circle-dot-dashed',
      label: t('CONVERSATION.RESOLVE_DROPDOWN.MARK_PENDING'),
      action: 'mark_pending',
      value: 'pending',
    });
  }

  return items;
});

const actionMenuItems = computed(() =>
  isSmallScreen.value
    ? [...resolveMenuItems.value, ...baseActionMenuItems.value]
    : baseActionMenuItems.value
);

const toggleStatus = (status, snoozedUntil = null, caseCloseType = null) => {
  store.dispatch('toggleStatus', {
    conversationId: currentChat.value.id,
    status,
    snoozedUntil,
    caseCloseType,
  });
  useAlert(t('CONVERSATION.CHANGE_STATUS'));
};

const openSnoozeModal = () => {
  const ninja = document.querySelector('ninja-keys');
  ninja?.open({ parent: 'snooze_conversation' });
};

const handleActionClick = ({ action, value }) => {
  toggleDropdown(false);

  if (action === 'resolve_case') {
    toggleStatus(wootConstants.STATUS_TYPE.RESOLVED, null, value);
  } else if (action === 'open_conversation') {
    toggleStatus(wootConstants.STATUS_TYPE.OPEN);
  } else if (action === 'snooze_conversation') {
    openSnoozeModal();
  } else if (action === 'mark_pending') {
    toggleStatus(wootConstants.STATUS_TYPE.PENDING);
  } else if (action === 'mute') {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  }
};

// These functions are needed for the event listeners
const mute = () => {
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <ResolveAction
      v-if="!isSmallScreen"
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="actionMenuItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
  </div>
</template>
